/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import { CircleMinus, CirclePlus, Plus } from '@saleshandy/icons';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import Checkbox from '../../../../../shared/design-system/components/atoms/checkbox';
import {
  OverlayTooltip,
  Placement,
} from '../../../../../shared/design-system/components/overlay';
import Tag from '../../../../../shared/design-system/components/tag';
import {
  deepClone,
  findParentKey,
  getExceptFilterValues,
  getFilterKeys,
  getFilterValuesWithExtraValues,
  LOCAL_SEARCH_MAPPED_KEYS,
  MAPPED_KEYS,
  NOT_ALLOWED_APPEND_CHECKBOXES,
  V2_MAPPED_KEYS,
} from '../../../helpers/helper';
import {
  DISABLED_BY_UNKNOWN_TOOLTIP,
  FilterComponentType,
  GetV2FilterSuggestionResponse,
} from '../../../types';
import {
  getFilterSuggestionAPI,
  getV2FilterSuggestionAPI,
  getCompanyNameDomainAPI,
} from '../../../helpers/leads.api';
import Icon from '../../../../../shared/design-system/components/atoms/icon/icon';
import {
  findAllInstancesByValue,
  performLocalSearch,
  calculateCheckState,
  getCheckedKeys,
  SuggestionItem,
  getSuggestionValue,
  isCompanySuggestion,
} from './v2-filter-checkbox.helpers';
import {
  RenderAutocomplete,
  RenderCheckboxGroup,
  getLabelTooltip,
} from './v2-filter-checkbox.components';
import {
  CheckboxItem,
  FilterCheckboxProps,
  HandleOnChangeParams,
} from './v2-filter-checkbox.types';

const V2FilterCheckbox: React.FC<FilterCheckboxProps> = ({
  subFilter,
  handleQuery,
  isExpand = false,
  parentKey = '',
  hideIncludeExclude = false,
  bulkFilterSyncKey = {},
  handleExpandFilterTags,
  isPhoneNumberNudgeOpen = false,
  searchLeads,
  pendingFilters,
  applyPendingFilters,
  isDisabledByUnknown = false,
  companyIdNameMap,
  setCompanyIdNameMap,
  onLockedClick,
}) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location?.search);
  const inputRef = useRef(null);
  const pendingFiltersRef = useRef<string>('');
  const pendingFiltersRefObj = useRef<URLSearchParams | null>(null);

  const {
    key: filterKey,
    list = [],
    inputClassName = '',
    inputPlaceHolder = '',
    inputLabel = '',
    hideSearchBox = false,
    removeTopMargin = false,
    infoCircleToolTipMapping = {},
    includeMaxLimit,
    expandedMaxFilterTags,
    advanceFilters,
    disableCustomInput = false,
  } = subFilter;

  const getParamsToRead = (): URLSearchParams => {
    if (pendingFilters != null) {
      const pendingString = pendingFilters.toString();
      pendingFiltersRef.current = pendingString;
      pendingFiltersRefObj.current = pendingFilters;
      return pendingFilters;
    }
    return queryParams;
  };

  const excludeKey = `exclude${filterKey
    .charAt(0)
    .toUpperCase()}${filterKey.slice(1)}`;

  const handleInitializeCheckboxes = (mainCheckbox: boolean) => {
    const paramsToRead = getParamsToRead();
    const includedKeys = paramsToRead
      .getAll(filterKey)
      .map((item) => item?.replace(/"/g, ''));
    const excludedKeys = paramsToRead
      .getAll(excludeKey)
      .map((item) => item?.replace(/"/g, ''));

    const baseCheckboxes = list.map((item) => {
      const parentIncluded = includedKeys.includes(item?.key);
      const parentExcluded = excludedKeys.includes(item?.key);

      let children = [];
      if (item?.children?.length > 0) {
        children = item.children.map((child) => {
          const childIncluded = includedKeys.includes(child?.key);
          const childExcluded = excludedKeys.includes(child?.key);
          const checked =
            childIncluded || childExcluded || parentIncluded || parentExcluded;

          return {
            ...child,
            checked: checked ?? false,
            isExcluded: childExcluded || false,
          };
        });
      }

      const childrenCheckedCount = children?.filter((c) => c?.checked)?.length;
      const isIntermediate =
        childrenCheckedCount >= 1 &&
        item?.children?.length !== childrenCheckedCount;

      return {
        ...item,
        checked: isIntermediate
          ? 'indeterminate'
          : !!(parentIncluded || parentExcluded),
        isExcluded: parentExcluded || false,
        children,
      };
    });

    if (!NOT_ALLOWED_APPEND_CHECKBOXES?.includes(filterKey) && mainCheckbox) {
      const exceptListValues = getExceptFilterValues(includedKeys, list);
      const includedCustomCheckboxes = exceptListValues.map((value) => ({
        key: value,
        value,
        checked: true,
        isExcluded: false,
        children: [],
      }));

      const exceptExcludeListValues = getExceptFilterValues(excludedKeys, list);
      const excludedCustomCheckboxes = exceptExcludeListValues.map((value) => ({
        key: value,
        value,
        checked: true,
        isExcluded: true,
        children: [],
      }));

      return [
        ...baseCheckboxes,
        ...includedCustomCheckboxes,
        ...excludedCustomCheckboxes,
      ];
    }

    return baseCheckboxes;
  };

  // Use companyIdNameMap from props, fallback to empty object if not provided
  const effectiveCompanyIdNameMap = companyIdNameMap ?? {};

  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState<string>('');
  const [checkboxes, setCheckboxes] = useState(() =>
    handleInitializeCheckboxes(true),
  );
  const [initialCheckboxes] = useState(() => handleInitializeCheckboxes(false));
  const [expanded, setExpanded] = useState([]);
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [showAutoComplete, setShowAutoComplete] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [isV2ResponseType, setIsV2ResponseType] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [exactMatch, setExactMatch] = useState<boolean>(() => {
    const paramsToRead = getParamsToRead();
    return paramsToRead.get(`${filterKey}_exactMatch`) === '1';
  });
  const [shouldSearchPastValues, setShouldSearchPastValues] = useState<boolean>(
    () => {
      const paramsToRead = getParamsToRead();
      return (
        paramsToRead.get(`${filterKey}_shouldSearchPast`) === '1' ||
        (filterKey === 'company_ids' &&
          paramsToRead.get('company_name_shouldSearchPast') === '1')
      );
    },
  );
  const [onFirstTimeKeyEnter, setOnFirstTimeKeyEnter] = useState<boolean>(
    false,
  );
  const [showTagTooltip, setShowTagTooltip] = useState<string | null>(null);

  const searchInputContainerId = `filter-${filterKey}-input`;
  const suggestionsContainerID = `autocomplete-${filterKey}`;

  const paramsToRead = getParamsToRead();
  const isIncludeDisabled =
    includeMaxLimit &&
    paramsToRead.getAll(filterKey)?.length >= includeMaxLimit;
  const includeTooltip = isIncludeDisabled
    ? `${includeMaxLimit} keywords already included`
    : 'Include';

  const companyNameIncludeValues =
    filterKey === 'company_ids'
      ? paramsToRead
          .getAll('company_name')
          .map((item) => item?.replace(/"/g, ''))
          .filter(Boolean)
      : [];
  const companyNameExcludeValues =
    filterKey === 'company_ids'
      ? paramsToRead
          .getAll('excludeCompany_name')
          .map((item) => item?.replace(/"/g, ''))
          .filter(Boolean)
      : [];

  const handlePreQuery = (
    updatedCheckboxes,
    showExactMatch?: boolean,
    showIncludePastJobs?: boolean,
    isSearchLeads?: boolean,
    onKeyEnter = false,
    competitorCompanyIdParam?: number,
  ) => {
    const includedKeys = getCheckedKeys(
      updatedCheckboxes,
      true,
      showExactMatch,
    );
    const excludedKeys = getCheckedKeys(
      updatedCheckboxes,
      false,
      showExactMatch,
    );

    const finalIncludedKeys =
      filterKey === 'company_ids'
        ? [...includedKeys, ...companyNameIncludeValues]
        : includedKeys;
    const finalExcludedKeys =
      filterKey === 'company_ids'
        ? [...excludedKeys, ...companyNameExcludeValues]
        : excludedKeys;

    handleQuery({
      filterKey,
      filterExcludeKey: excludeKey,
      filterValue: {
        includedKeys: finalIncludedKeys,
        excludedKeys: finalExcludedKeys,
      },
      filterType: FilterComponentType?.MULTILEVEL_CHECKBOX,
      showExactMatch,
      showIncludePastJobs,
      isSearchLeads,
      onKeyEnter,
      competitorCompanyId: competitorCompanyIdParam,
    });
  };

  useEffect(() => {
    if (bulkFilterSyncKey?.[filterKey]) {
      setCheckboxes(handleInitializeCheckboxes(true));
    }
  }, [bulkFilterSyncKey, filterKey]);

  useEffect(() => {
    setCheckboxes(handleInitializeCheckboxes(true));
    if (pendingFilters != null) {
      const pendingString = pendingFilters.toString();
      pendingFiltersRef.current = pendingString;
      pendingFiltersRefObj.current = pendingFilters;
    } else {
      pendingFiltersRef.current = '';
      pendingFiltersRefObj.current = null;
    }
  }, [location?.search, filterKey, pendingFilters]);

  const handleOnChange = ({
    action = 'changeParent',
    groupName = null,
    childName = null,
    exclude = null,
    isSearchLeads = false,
    onKeyEnter = false,
    competitorCompanyId,
  }: HandleOnChangeParams) => {
    const shouldApplyImmediatelyOnRemove =
      action === 'removeTag' && !!applyPendingFilters;
    const effectiveOnKeyEnter = onKeyEnter || shouldApplyImmediatelyOnRemove;
    const effectiveIsSearchLeads =
      isSearchLeads || shouldApplyImmediatelyOnRemove;

    if (!groupName && isSearchLeads && onKeyEnter) {
      searchLeads(null, isSearchLeads);
      return;
    }

    let groupIndex = -1;
    let childIndex = -1;
    if (groupName) {
      groupIndex = checkboxes?.findIndex((c) => c?.key === groupName);
    }
    if (
      childName &&
      groupIndex > -1 &&
      checkboxes[groupIndex] &&
      checkboxes[groupIndex]?.children?.length > 0
    ) {
      childIndex = checkboxes[groupIndex]?.children?.findIndex(
        (c) => c?.key === childName,
      );
    }

    setCheckboxes((prevState) => {
      let updatedValues = deepClone(prevState);
      const currentParams = getParamsToRead();

      // Skip location preservation when removing a tag - we don't want to re-add it
      if (
        (filterKey === 'location' || filterKey === 'excludeLocation') &&
        action !== 'removeTag'
      ) {
        const existingIndividualTagsValues =
          currentParams
            .getAll(filterKey)
            ?.map((item) => item.replace(/"/g, '')) || [];

        let tagValues = Array.from(new Set([...existingIndividualTagsValues]));

        const filteredObjects = updatedValues.filter((obj) => obj.checked);

        const keysToRemove = new Set();
        filteredObjects.forEach((obj) => {
          keysToRemove.add(obj.key); // Add the main key
          if (obj.children) {
            obj.children.forEach((child) => keysToRemove.add(child.key));
          }
        });

        tagValues = tagValues.filter((item) => !keysToRemove.has(item));

        tagValues.forEach((tag) => {
          const isTagExcluded = currentParams
            .getAll(excludeKey)
            .map((item) => item.replace(/"/g, ''))
            .includes(tag);
          updatedValues.push({
            key: tag,
            value: tag,
            checked: true,
            isExcluded: isTagExcluded,
            children: [],
          });
        });
      }

      const updateItemsWithInstanceKeys = (
        items: any[],
        instanceKeys: Set<string>,
        isExclude: boolean,
      ) => {
        let checkedCount = 0;
        const updated = items.map((item) => {
          const updatedItem = deepClone(item);

          if (instanceKeys.has(item.key)) {
            updatedItem.checked = true;
            checkedCount = 1;
            updatedItem.isExcluded = isExclude;

            if (updatedItem.children?.length > 0) {
              updatedItem.children = updatedItem.children.map((child) => ({
                ...child,
                checked: true,
                isExcluded: isExclude,
              }));
            }
          } else if (item.children?.length > 0) {
            let hasMatchingChild = false;
            updatedItem.children = updatedItem.children.map((child) => {
              if (instanceKeys.has(child.key)) {
                hasMatchingChild = true;
                checkedCount = 1;
                return { ...child, checked: true, isExcluded: isExclude };
              }
              return child;
            });

            if (hasMatchingChild) {
              const checkedChildren = updatedItem.children.filter(
                (c) => c.checked,
              ).length;
              updatedItem.checked = calculateCheckState({
                checkedChild: checkedChildren,
                totalChild: updatedItem.children.length,
              });
            }
          }

          return updatedItem;
        });
        return { updated, checkedCount };
      };

      const includeExcludeOptions = (isExclude: boolean) => {
        let checkedCount = 0;
        const allInstances = findAllInstancesByValue(groupName, list);

        if (allInstances.length > 0) {
          const instanceKeys = new Set<string>();
          allInstances.forEach((instance) => {
            instanceKeys.add(instance.childKey);
            if (instance.parentKey === instance.childKey) {
              instanceKeys.add(instance.parentKey);
            }
          });

          const result = updateItemsWithInstanceKeys(
            updatedValues,
            instanceKeys,
            isExclude,
          );
          updatedValues = result.updated;
          checkedCount = result.checkedCount;
        } else {
          const tagInputKey = getFilterKeys([groupName], list)[0] || groupName;
          const newGroupIndex = tagInputKey
            ? checkboxes?.findIndex((c) => c?.key === tagInputKey)
            : -1;

          updatedValues = updatedValues.map((item) => {
            const updatedItem = deepClone(item);

            if (newGroupIndex > -1 && item?.key === tagInputKey) {
              updatedItem.checked = true;
              checkedCount = 1;
              updatedItem.isExcluded = isExclude;
            } else {
              const foundParentKey = findParentKey(list, tagInputKey);
              if (foundParentKey) {
                const foundGroupIndex = checkboxes?.findIndex(
                  (c) => c?.key === foundParentKey,
                );
                if (
                  tagInputKey &&
                  foundGroupIndex > -1 &&
                  checkboxes[foundGroupIndex]?.children?.length > 0
                ) {
                  const foundChildIndex = checkboxes[
                    foundGroupIndex
                  ].children.findIndex((c) => c?.key === tagInputKey);
                  if (foundGroupIndex > -1 && item?.key === foundParentKey) {
                    updatedItem.children = updatedItem.children?.map(
                      (child, index) => {
                        const childItem = deepClone(child);
                        if (index === foundChildIndex) {
                          childItem.checked = true;
                          checkedCount = 1;
                          childItem.isExcluded = isExclude;
                        }
                        return childItem;
                      },
                    );

                    const checkedChildren = updatedItem.children.filter(
                      (c) => c.checked,
                    ).length;
                    updatedItem.checked = calculateCheckState({
                      checkedChild: checkedChildren,
                      totalChild: updatedItem.children.length,
                    });

                    const excludedChildren = updatedItem.children.filter(
                      (c) => c.isExcluded,
                    ).length;
                    updatedItem.isExcluded = excludedChildren > 0;
                  }
                }
              }
            }
            return updatedItem;
          });

          if (
            !NOT_ALLOWED_APPEND_CHECKBOXES?.includes(filterKey) &&
            checkedCount === 0
          ) {
            updatedValues.push({
              key: tagInputKey,
              value: tagInputKey,
              checked: true,
              isExcluded: isExclude,
              children: [],
            });
          }
        }

        setInputValue('');
      };

      switch (action) {
        case 'changeParent':
          if (groupIndex > -1) {
            updatedValues[groupIndex] = {
              ...updatedValues[groupIndex],
              checked: !prevState[groupIndex]?.checked,
              children: prevState[groupIndex]?.children?.map((child) => ({
                ...child,
                checked: !prevState[groupIndex]?.checked,
                isExcluded: false,
              })),
            };
          }
          break;
        case 'changeChild':
          if (groupIndex > -1 && childIndex > -1) {
            const clickedChild = prevState[groupIndex]?.children[childIndex];
            const clickedChildValue = clickedChild?.value || clickedChild?.key;
            const newCheckedState = !clickedChild?.checked;

            const allInstances = findAllInstancesByValue(
              clickedChildValue,
              list,
            );
            const instanceKeys = new Set<string>();
            allInstances.forEach((instance) => {
              instanceKeys.add(instance.childKey);
            });

            updatedValues = updatedValues.map((item) => {
              const updatedItem = deepClone(item);

              if (item.children?.length > 0) {
                const hasMatchingChild = item.children.some((child) =>
                  instanceKeys.has(child.key),
                );

                if (hasMatchingChild) {
                  const parentIsFullyIncluded =
                    item.checked === true && item.isExcluded === false;
                  const parentIsFullyExcluded =
                    item.checked === true && item.isExcluded === true;

                  updatedItem.children = updatedItem.children.map((child) => {
                    if (instanceKeys.has(child.key)) {
                      return {
                        ...child,
                        checked: newCheckedState,
                        isExcluded: newCheckedState
                          ? item.isExcluded || false
                          : false,
                      };
                    }
                    // If unchecking and parent was fully included/excluded,
                    // siblings should remain in parent's action (explicitly set)
                    if (
                      !newCheckedState &&
                      (parentIsFullyIncluded || parentIsFullyExcluded)
                    ) {
                      return {
                        ...child,
                        isExcluded: parentIsFullyExcluded,
                      };
                    }
                    return child;
                  });

                  const checkedChildren = updatedItem.children.filter(
                    (c) => c.checked,
                  ).length;
                  const excludedChildren = updatedItem.children.filter(
                    (c) => c.isExcluded,
                  ).length;
                  const includedChildren = updatedItem.children.filter(
                    (c) => c.checked && !c.isExcluded,
                  ).length;

                  // Parent should be indeterminate if children have mixed actions
                  const hasMixedActions =
                    includedChildren > 0 && excludedChildren > 0;

                  if (hasMixedActions) {
                    updatedItem.checked = 'indeterminate';
                    updatedItem.isExcluded = false;
                  } else {
                    updatedItem.checked = calculateCheckState({
                      checkedChild: checkedChildren,
                      totalChild: updatedItem.children.length,
                    });
                    // Parent is excluded only if ALL checked children are excluded
                    updatedItem.isExcluded =
                      checkedChildren > 0 &&
                      excludedChildren === checkedChildren;
                  }
                }
              } else if (instanceKeys.has(item.key)) {
                updatedItem.checked = newCheckedState;
                updatedItem.isExcluded = newCheckedState
                  ? updatedItem.isExcluded
                  : false;
              }

              return updatedItem;
            });
          }
          break;
        case 'includeParent':
          if (groupIndex > -1) {
            updatedValues[groupIndex] = {
              ...updatedValues[groupIndex],
              checked: true,
              isExcluded: false,
              children: prevState[groupIndex]?.children?.map((child) => ({
                ...child,
                checked: true,
                isExcluded: false,
              })),
            };
          }
          break;
        case 'includeChild':
          if (groupIndex > -1 && childIndex > -1) {
            const clickedChild = prevState[groupIndex]?.children[childIndex];
            const clickedChildValue = clickedChild?.value || clickedChild?.key;

            const allInstances = findAllInstancesByValue(
              clickedChildValue,
              list,
            );
            const instanceKeys = new Set<string>();
            allInstances.forEach((instance) => {
              instanceKeys.add(instance.childKey);
            });

            updatedValues = updatedValues.map((item) => {
              const updatedItem = deepClone(item);

              if (item.children?.length > 0) {
                // Check if any child in this parent matches the target
                const hasMatchingChild = item.children.some((child) =>
                  instanceKeys.has(child.key),
                );

                if (hasMatchingChild) {
                  const parentIsFullyExcluded =
                    item.checked === true && item.isExcluded === true;

                  updatedItem.children = updatedItem.children.map((child) => {
                    if (instanceKeys.has(child.key)) {
                      // Target child: include it
                      return { ...child, checked: true, isExcluded: false };
                    }
                    // Siblings: if parent was fully excluded, explicitly set siblings to excluded
                    // Otherwise, preserve sibling state (when parent is indeterminate)
                    if (parentIsFullyExcluded) {
                      return { ...child, checked: true, isExcluded: true };
                    }
                    // When parent is indeterminate, preserve sibling state
                    return child;
                  });

                  const checkedChildren = updatedItem.children.filter(
                    (c) => c.checked,
                  ).length;
                  const excludedChildren = updatedItem.children.filter(
                    (c) => c.isExcluded,
                  ).length;
                  const includedChildren = updatedItem.children.filter(
                    (c) => c.checked && !c.isExcluded,
                  ).length;

                  // Parent should be indeterminate if children have mixed actions
                  const hasMixedActions =
                    includedChildren > 0 && excludedChildren > 0;

                  if (hasMixedActions) {
                    updatedItem.checked = 'indeterminate';
                    updatedItem.isExcluded = false;
                  } else if (
                    includedChildren === checkedChildren &&
                    checkedChildren === updatedItem.children.length
                  ) {
                    // All children are included - parent should be fully included
                    updatedItem.checked = true;
                    updatedItem.isExcluded = false;
                  } else {
                    updatedItem.checked = calculateCheckState({
                      checkedChild: checkedChildren,
                      totalChild: updatedItem.children.length,
                    });
                    // Parent is excluded only if ALL checked children are excluded
                    updatedItem.isExcluded =
                      checkedChildren > 0 &&
                      excludedChildren === checkedChildren;
                  }
                }
              } else if (instanceKeys.has(item.key)) {
                updatedItem.checked = true;
                updatedItem.isExcluded = false;
              }

              return updatedItem;
            });
          }
          break;
        case 'excludeParent':
          if (groupIndex > -1) {
            updatedValues[groupIndex] = {
              ...updatedValues[groupIndex],
              checked: true,
              isExcluded: true,
              children: prevState[groupIndex]?.children?.map((child) => ({
                ...child,
                checked: true,
                isExcluded: true,
              })),
            };
          }
          break;
        case 'excludeChild':
          if (groupIndex > -1 && childIndex > -1) {
            const clickedChild = prevState[groupIndex]?.children[childIndex];
            const clickedChildValue = clickedChild?.value || clickedChild?.key;

            const allInstances = findAllInstancesByValue(
              clickedChildValue,
              list,
            );
            const instanceKeys = new Set<string>();
            allInstances.forEach((instance) => {
              instanceKeys.add(instance.childKey);
            });

            updatedValues = updatedValues.map((item) => {
              const updatedItem = deepClone(item);

              if (item.children?.length > 0) {
                // Check if any child in this parent matches the target
                const hasMatchingChild = item.children.some((child) =>
                  instanceKeys.has(child.key),
                );

                if (hasMatchingChild) {
                  const parentIsFullyIncluded =
                    item.checked === true && item.isExcluded === false;

                  updatedItem.children = updatedItem.children.map((child) => {
                    if (instanceKeys.has(child.key)) {
                      // Target child: exclude it
                      return { ...child, checked: true, isExcluded: true };
                    }
                    // Siblings: if parent was fully included, explicitly set siblings to included
                    // Otherwise, preserve sibling state (when parent is indeterminate)
                    if (parentIsFullyIncluded) {
                      return { ...child, checked: true, isExcluded: false };
                    }
                    // When parent is indeterminate, preserve sibling state
                    return child;
                  });

                  const checkedChildren = updatedItem.children.filter(
                    (c) => c.checked,
                  ).length;
                  const excludedChildren = updatedItem.children.filter(
                    (c) => c.isExcluded,
                  ).length;
                  const includedChildren = updatedItem.children.filter(
                    (c) => c.checked && !c.isExcluded,
                  ).length;

                  // Parent should be indeterminate if children have mixed actions
                  const hasMixedActions =
                    includedChildren > 0 && excludedChildren > 0;

                  if (hasMixedActions) {
                    updatedItem.checked = 'indeterminate';
                    updatedItem.isExcluded = false;
                  } else if (
                    excludedChildren === checkedChildren &&
                    checkedChildren === updatedItem.children.length
                  ) {
                    // All children are excluded - parent should be fully excluded
                    updatedItem.checked = true;
                    updatedItem.isExcluded = true;
                  } else {
                    updatedItem.checked = calculateCheckState({
                      checkedChild: checkedChildren,
                      totalChild: updatedItem.children.length,
                    });
                    // Parent is excluded only if ALL checked children are excluded
                    updatedItem.isExcluded =
                      checkedChildren > 0 &&
                      excludedChildren === checkedChildren;
                  }
                }
              } else if (instanceKeys.has(item.key)) {
                updatedItem.checked = true;
                updatedItem.isExcluded = true;
              }

              return updatedItem;
            });
          }
          break;
        case 'removeTag': {
          const removeParams = getParamsToRead();
          const tagKey = removeParams
            .getAll(groupName)
            ?.map((item) => item?.replace(/"/g, '').substring(0, 2));

          if (tagKey?.length > 0) {
            tagKey.forEach((tag) => {
              const tagIndex = checkboxes.findIndex(
                (item) => item?.key?.substring(0, 2) === tag,
              );

              if (tagIndex > -1) {
                if (updatedValues[tagIndex].checked !== 'indeterminate') {
                  updatedValues[tagIndex] = {
                    ...updatedValues[tagIndex],
                    checked: false,
                    children: prevState[tagIndex]?.children?.map((child) => ({
                      ...child,
                      checked: false,
                    })),
                  };
                } else {
                  updatedValues[tagIndex] = {
                    ...updatedValues[tagIndex],
                    children: prevState[tagIndex]?.children?.map((child) =>
                      child.isExcluded === exclude
                        ? { ...child, checked: false }
                        : child,
                    ),
                  };
                }
              }
            });
          } else {
            const tagValue = groupName.replace(/"/g, '');
            const allInstances = findAllInstancesByValue(tagValue, list);

            if (allInstances.length > 0) {
              const instanceKeys = new Set<string>();
              allInstances.forEach((instance) => {
                instanceKeys.add(instance.childKey);
              });

              updatedValues = updatedValues.map((item) => {
                const updatedItem = deepClone(item);

                if (instanceKeys.has(item.key)) {
                  updatedItem.checked = false;
                  updatedItem.isExcluded = false;
                  updatedItem.children = updatedItem.children.map((child) => ({
                    ...child,
                    checked: false,
                    isExcluded: false,
                  }));
                } else if (item.children?.length > 0) {
                  let hasUpdatedChild = false;
                  updatedItem.children = updatedItem.children.map((child) => {
                    if (instanceKeys.has(child.key)) {
                      hasUpdatedChild = true;
                      return { ...child, checked: false, isExcluded: false };
                    }
                    return child;
                  });

                  if (hasUpdatedChild) {
                    const checkedChildren = updatedItem.children.filter(
                      (c) => c.checked,
                    ).length;
                    updatedItem.checked = calculateCheckState({
                      checkedChild: checkedChildren,
                      totalChild: updatedItem.children.length,
                    });
                    updatedItem.isExcluded = false;
                  }
                }

                return updatedItem;
              });
            } else {
              const foundTagKey =
                getFilterKeys([groupName], list)[0] || groupName;
              let localGroupIndex = foundTagKey.replace(/"/g, '')
                ? checkboxes?.findIndex(
                    (c) => c?.key === foundTagKey?.replace(/"/g, ''),
                  )
                : -1;

              if (localGroupIndex > -1) {
                updatedValues[localGroupIndex] = {
                  ...updatedValues[localGroupIndex],
                  checked: false,
                  children: prevState[localGroupIndex]?.children?.map(
                    (child) => ({
                      ...child,
                      checked: false,
                    }),
                  ),
                };
              } else {
                const customTagKey =
                  getFilterKeys([groupName], list)[0] || groupName;
                const foundParentKey = findParentKey(list, customTagKey);
                if (foundParentKey) {
                  localGroupIndex = checkboxes?.findIndex(
                    (c) => c?.key === foundParentKey,
                  );
                }
                if (
                  customTagKey &&
                  localGroupIndex > -1 &&
                  checkboxes[localGroupIndex]?.children?.length > 0
                ) {
                  const localChildIndex = checkboxes[
                    localGroupIndex
                  ].children.findIndex((c) => c?.key === customTagKey);
                  updatedValues[localGroupIndex] = {
                    ...updatedValues[localGroupIndex],
                    children: prevState[
                      localGroupIndex
                    ]?.children?.map((child, cIndex) =>
                      cIndex === localChildIndex
                        ? { ...child, checked: false, isExcluded: false }
                        : child,
                    ),
                  };
                }
              }
            }
          }
          break;
        }
        case 'inputOption':
          includeExcludeOptions(false);
          break;
        case 'excludeOption':
          includeExcludeOptions(true);
          break;
        default:
          break;
      }

      const filteredUpdatedValues = [];
      const uniqueKeys = new Set();

      updatedValues.forEach((item) => {
        if (!uniqueKeys.has(item.key)) {
          uniqueKeys.add(item.key);
          filteredUpdatedValues.push(item);
        }
      });

      const allDeSelected = filteredUpdatedValues.every(
        (item) => !item.checked,
      );

      if (allDeSelected) {
        setExactMatch(false);
        setShouldSearchPastValues(false);
      }

      const latestExactMatch = allDeSelected ? false : exactMatch;
      const latestShouldSearchPastValues = allDeSelected
        ? false
        : shouldSearchPastValues;

      handlePreQuery(
        filteredUpdatedValues,
        latestExactMatch,
        latestShouldSearchPastValues,
        effectiveIsSearchLeads,
        effectiveOnKeyEnter,
        competitorCompanyId,
      );

      return filteredUpdatedValues;
    });
  };

  const debouncedInputChangeRef = useRef<ReturnType<typeof debounce>>();

  const handleInputChange = async (search: string) => {
    try {
      const trimmedSearch = String(search || '').trim();
      if (trimmedSearch.length > 50) {
        // Do not call suggestion APIs for excessively long input
        // Only show a validation error for company name inputs
        if (filterKey === 'company_ids') {
          setInputError('Company name must be at most 50 characters.');
        }
        setIsLoading(false);
        setShowAutoComplete(false);
        setSuggestions([]);
        return;
      }
      setInputError('');

      if (search.trim() === '') {
        setIsLoading(false);
        setShowAutoComplete(false);
        setSelectedSuggestionIndex(-1);
        return;
      }

      const companyKeys = ['company_ids'];
      if (companyKeys.includes(filterKey) && String(search).trim().length < 3) {
        setIsLoading(false);
        setShowAutoComplete(false);
        setSuggestions([]);
        setSelectedSuggestionIndex(-1);
        return;
      }

      const searchInputValue =
        (document?.getElementById(searchInputContainerId) as HTMLInputElement)
          ?.value || inputValue;

      setIsLoading(true);
      setShowAutoComplete(true);

      if (filterKey === 'company_ids') {
        const res: any = await getCompanyNameDomainAPI({
          searchText: search,
        });
        setIsLoading(false);
        if (
          res?.payload &&
          Array.isArray(res.payload) &&
          searchInputValue.trim() !== ''
        ) {
          setSuggestions(res.payload);
          setIsV2ResponseType(false);
          setSelectedSuggestionIndex(-1);
        } else {
          setShowAutoComplete(false);
        }
      } else if (filterKey === 'company_domain') {
        //   const res: any = await getCompanyNameDomainAPI({
        //     searchText: search,
        //   });
        //   setIsLoading(false);
        //   if (
        //     res?.payload &&
        //     Array.isArray(res.payload) &&
        //     searchInputValue.trim() !== ''
        //   ) {
        //     const { payload } = res;
        //     const withDomain = payload.filter(
        //       (s: any) => isCompanySuggestion(s) && s.companyDomain,
        //     );
        //     setSuggestions(withDomain.length > 0 ? withDomain : payload);
        //     setIsV2ResponseType(false);
        //     setSelectedSuggestionIndex(-1);
        //   } else {
        //     setShowAutoComplete(false);
        setShowAutoComplete(false);
        setSuggestions([]);
        setSelectedSuggestionIndex(-1);
      } else if (filterKey === 'competitors_company_name') {
        const res: any = await getCompanyNameDomainAPI({
          searchText: search,
        });
        setIsLoading(false);
        if (
          res?.payload &&
          Array.isArray(res.payload) &&
          searchInputValue.trim() !== ''
        ) {
          setSuggestions(res.payload);
          setIsV2ResponseType(false);
          setSelectedSuggestionIndex(-1);
        } else {
          setShowAutoComplete(false);
        }
      } else if (filterKey === 'website_domain') {
        const res: any = await getCompanyNameDomainAPI({
          searchText: search,
        });
        setIsLoading(false);
        if (
          res?.payload &&
          Array.isArray(res.payload) &&
          searchInputValue.trim() !== ''
        ) {
          const { payload } = res;
          const withDomain = payload.filter(
            (s: any) => isCompanySuggestion(s) && s.companyDomain,
          );
          setSuggestions(withDomain.length > 0 ? withDomain : payload);
          setIsV2ResponseType(false);
          setSelectedSuggestionIndex(-1);
        } else {
          setShowAutoComplete(false);
        }
      } else if (filterKey in V2_MAPPED_KEYS) {
        const payload = {
          type: V2_MAPPED_KEYS[filterKey],
          search,
          paramType:
            filterKey === 'visits_breakdown_by_country' ? 'country' : undefined,
        };
        const res: GetV2FilterSuggestionResponse = await getV2FilterSuggestionAPI(
          payload,
        );
        setIsLoading(false);
        if (res?.payload && searchInputValue.trim() !== '') {
          setSuggestions(res.payload);
          setIsV2ResponseType(true);
          setSelectedSuggestionIndex(-1);
        } else {
          setShowAutoComplete(false);
        }
      } else if (filterKey in MAPPED_KEYS) {
        const res: any = await getFilterSuggestionAPI({
          type: MAPPED_KEYS[filterKey],
          search,
        });
        setIsLoading(false);
        if (res?.payload && searchInputValue.trim() !== '') {
          setSuggestions(res.payload);
          setIsV2ResponseType(false);
          setSelectedSuggestionIndex(-1);
        } else {
          setShowAutoComplete(false);
        }
      } else if (filterKey in LOCAL_SEARCH_MAPPED_KEYS) {
        const dataObject = LOCAL_SEARCH_MAPPED_KEYS[filterKey];
        const localResults = performLocalSearch(search, dataObject, 15);
        setIsLoading(false);
        if (localResults.length > 0 && searchInputValue.trim() !== '') {
          setSuggestions(localResults);
          setIsV2ResponseType(false);
          setSelectedSuggestionIndex(-1);
        } else {
          setShowAutoComplete(false);
          setSelectedSuggestionIndex(-1);
          setSuggestions([]);
        }
      } else {
        setIsLoading(false);
        setShowAutoComplete(false);
      }
    } catch (e) {
      setIsLoading(false);
      setShowAutoComplete(false);
    }
  };

  useEffect(() => {
    debouncedInputChangeRef.current = debounce(handleInputChange, 500);

    return () => {
      debouncedInputChangeRef.current?.cancel();
    };
  }, [filterKey]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const direction = e.key === 'ArrowDown' ? 1 : -1;
      const newIndex = selectedSuggestionIndex + direction;

      if (newIndex >= 0 && newIndex < suggestions?.length) {
        setSelectedSuggestionIndex(newIndex);
      }
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (isIncludeDisabled) return;

      const hasSuggestionSelected =
        selectedSuggestionIndex !== -1 ||
        (suggestions?.length > 0 && disableCustomInput);

      if (hasSuggestionSelected) {
        const selectedSuggestion =
          suggestions[selectedSuggestionIndex] || suggestions[0];
        const suggestionValue = getSuggestionValue(selectedSuggestion);

        // For company_ids, use companyId as groupName and update map
        // For company_domain / website_domain, use companyDomain; skip if empty
        // For competitors_company_name, use companyName
        let groupNameToUse = suggestionValue;
        if (
          filterKey === 'company_ids' &&
          isCompanySuggestion(selectedSuggestion)
        ) {
          groupNameToUse = String(selectedSuggestion.companyId);
          setCompanyIdNameMap?.((prev) => ({
            ...prev,
            [groupNameToUse]: selectedSuggestion.companyName,
          }));
        } else if (
          filterKey === 'competitors_company_name' &&
          isCompanySuggestion(selectedSuggestion)
        ) {
          groupNameToUse = selectedSuggestion.companyName;
        } else if (
          (filterKey === 'company_domain' || filterKey === 'website_domain') &&
          isCompanySuggestion(selectedSuggestion) &&
          selectedSuggestion.companyDomain
        ) {
          groupNameToUse = selectedSuggestion.companyDomain;
        } else if (
          (filterKey === 'company_domain' || filterKey === 'website_domain') &&
          isCompanySuggestion(selectedSuggestion) &&
          !selectedSuggestion.companyDomain
        ) {
          setSuggestions([]);
          setShowAutoComplete(false);
          setSelectedSuggestionIndex(-1);
          return;
        }

        let inputValueToSet = suggestionValue;
        if (
          (filterKey === 'company_domain' || filterKey === 'website_domain') &&
          isCompanySuggestion(selectedSuggestion)
        ) {
          inputValueToSet = selectedSuggestion.companyDomain;
        } else if (
          filterKey === 'competitors_company_name' &&
          isCompanySuggestion(selectedSuggestion)
        ) {
          inputValueToSet = selectedSuggestion.companyName;
        }
        setInputValue(inputValueToSet);
        setSuggestions([]);
        setShowAutoComplete(false);
        setOnFirstTimeKeyEnter(true);
        handleOnChange({
          action: 'inputOption',
          groupName: groupNameToUse,
          isSearchLeads: false,
          onKeyEnter: !!applyPendingFilters,
          competitorCompanyId:
            filterKey === 'competitors_company_name' &&
            isCompanySuggestion(selectedSuggestion)
              ? selectedSuggestion.companyId
              : undefined,
        });
        setSelectedSuggestionIndex(-1);
        return;
      }

      const trimmedValue = (e.target as HTMLInputElement)?.value.trim();
      if (trimmedValue === '') {
        if (applyPendingFilters) {
          applyPendingFilters();
        } else if (onFirstTimeKeyEnter) {
          setOnFirstTimeKeyEnter(false);
          handleOnChange({
            action: 'inputOption',
            groupName: trimmedValue,
            isSearchLeads: true,
            onKeyEnter: true,
          });
        }
        return;
      }

      if (disableCustomInput) return;

      setShowAutoComplete(false);
      debouncedInputChangeRef.current.cancel();
      setOnFirstTimeKeyEnter(true);
      handleOnChange({
        action: 'inputOption',
        groupName: trimmedValue,
        isSearchLeads: false,
        onKeyEnter: !!applyPendingFilters,
      });
    }
  };

  const handleShowExactMatch = (checked: boolean) => {
    if (isDisabledByUnknown) return;
    setExactMatch(checked);
    handlePreQuery(checkboxes, checked, shouldSearchPastValues, false, false);
  };

  const handleShowIncludePastJobs = (checked: boolean) => {
    if (isDisabledByUnknown) return;
    setShouldSearchPastValues(checked);
    handlePreQuery(checkboxes, exactMatch, checked, false, false);
  };

  const onSelectOption = (option: string) => {
    if (isIncludeDisabled) return;

    let competitorCompanyId: number | undefined;

    // For company_ids, update the map with ID -> name before proceeding
    if (filterKey === 'company_ids') {
      const companySuggestion = suggestions.find(
        (s) => isCompanySuggestion(s) && String(s.companyId) === option,
      );
      if (companySuggestion && isCompanySuggestion(companySuggestion)) {
        setCompanyIdNameMap?.((prev) => ({
          ...prev,
          [option]: companySuggestion.companyName,
        }));
      }
    }

    // For competitors_company_name, get companyId from suggestion
    if (filterKey === 'competitors_company_name') {
      const companySuggestion = suggestions.find(
        (s) => isCompanySuggestion(s) && s.companyName === option,
      );
      if (companySuggestion && isCompanySuggestion(companySuggestion)) {
        competitorCompanyId = companySuggestion.companyId;
      }
    }

    setSuggestions([]);
    setShowAutoComplete(false);
    handleOnChange({
      action: 'inputOption',
      groupName: option,
      isSearchLeads: false,
      competitorCompanyId,
    });
    setSelectedSuggestionIndex(-1);
  };

  const handleToggleExpand = (key: string) => {
    if (expanded.includes(key)) {
      setExpanded((d) => d.filter((i) => i !== key));
    } else {
      setExpanded((d) => [...d, key]);
    }
  };

  // Sync checkboxes with pendingFilters or URL params
  // This effect runs when location.search changes, and also when component re-renders
  // (which happens when pendingFilters changes in the parent)
  useEffect(() => {
    const currentParams = getParamsToRead();
    const includedItemsFromParams = currentParams
      .getAll(filterKey)
      .map((item) => item.replace(/"/g, ''));
    const excludedItemsFromParams = currentParams
      .getAll(excludeKey)
      .map((item) => item.replace(/"/g, ''));

    if (
      includedItemsFromParams.length === 0 &&
      excludedItemsFromParams.length === 0
    ) {
      setExpanded([]);
    }

    setCheckboxes((prevState) => {
      let newState = [];
      if (prevState?.length > 0) {
        newState = prevState.map((item) => {
          let children = [];
          if (item?.children?.length > 0) {
            children = item?.children?.map((child) => ({
              ...child,
              isExcluded: excludedItemsFromParams.includes(child?.key),
              checked:
                includedItemsFromParams.includes(item?.key) ||
                excludedItemsFromParams.includes(item?.key) ||
                includedItemsFromParams.includes(child?.key) ||
                excludedItemsFromParams.includes(child?.key),
            }));
          }
          const checkedChild = children.map((c) => c?.checked).filter(Boolean)
            .length;
          let parentChecked = calculateCheckState({
            checkedChild,
            totalChild: children.length,
          });

          // Check if parent key is explicitly in URL params
          const parentInParams =
            includedItemsFromParams.includes(item?.key) ||
            excludedItemsFromParams.includes(item?.key);

          if (parentInParams) {
            parentChecked = true;
          }

          // Set parent's isExcluded based on URL params
          const parentIsExcluded = excludedItemsFromParams.includes(item?.key);

          // If parent key is in URL params, use that state
          // Otherwise, calculate from children
          if (parentInParams) {
            return {
              ...item,
              checked: parentChecked,
              isExcluded: parentIsExcluded,
              children,
            };
          }

          // Parent not in URL params - calculate from children
          const includedChildren = children.filter(
            (c) => c.checked && !c.isExcluded,
          ).length;
          const excludedChildren = children.filter((c) => c.isExcluded).length;
          const hasMixedActions = includedChildren > 0 && excludedChildren > 0;

          if (hasMixedActions) {
            // Parent is indeterminate - isExcluded should be false
            return {
              ...item,
              checked: 'indeterminate',
              isExcluded: false,
              children,
            };
          }

          // All children in same state
          return {
            ...item,
            checked: parentChecked,
            isExcluded: excludedChildren === checkedChild && checkedChild > 0,
            children,
          };
        });
      }
      return newState;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.search, filterKey, excludeKey, pendingFilters]);

  useEffect(() => {
    if (inputRef?.current && isExpand) {
      if (
        ['role_and_department', 'company_industry', 'education'].includes(
          parentKey,
        )
      ) {
        if (['job_title', 'company_industry', 'major'].includes(filterKey)) {
          inputRef?.current.focus();
        }
      } else {
        inputRef?.current.focus();
      }
    }
  }, [isExpand, parentKey, filterKey]);

  useEffect(() => {
    if (typeof window === 'undefined' || hideSearchBox) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const targetDiv = document.getElementById(suggestionsContainerID);
      if (
        targetDiv &&
        event?.target &&
        event.target !== targetDiv &&
        !targetDiv.contains(event.target as Node)
      ) {
        setShowAutoComplete(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [hideSearchBox, suggestionsContainerID]);

  // for handling show exact match in referesh or location changes in url
  useEffect(() => {
    const currentParams = getParamsToRead();
    const exactMatchParam = currentParams.get(`${filterKey}_exactMatch`);
    if (exactMatchParam === '1') {
      setExactMatch(true);
    } else {
      setExactMatch(false);
    }
    const shouldSearchPastParam =
      currentParams.get(`${filterKey}_shouldSearchPast`) ||
      (filterKey === 'company_ids'
        ? currentParams.get('company_name_shouldSearchPast')
        : null);
    if (shouldSearchPastParam === '1') {
      setShouldSearchPastValues(true);
    } else {
      setShouldSearchPastValues(false);
    }
  }, [location?.search, filterKey, pendingFilters]);

  // Update checkbox values when companyIdNameMap changes (for company_ids)
  useEffect(() => {
    if (
      filterKey !== 'company_ids' ||
      Object.keys(effectiveCompanyIdNameMap).length === 0
    ) {
      return;
    }

    setCheckboxes((prevCheckboxes) =>
      prevCheckboxes.map((checkbox) => ({
        ...checkbox,
        value: effectiveCompanyIdNameMap[checkbox.key] || checkbox.value,
      })),
    );
  }, [effectiveCompanyIdNameMap, filterKey]);

  const filterValues = getFilterValuesWithExtraValues(
    paramsToRead.getAll(filterKey)?.map((item) => item?.replace(/"/g, '')),
    list,
  );
  const excludeValues = getFilterValuesWithExtraValues(
    paramsToRead.getAll(excludeKey)?.map((item) => item?.replace(/"/g, '')),
    list,
  );

  const updateCompanyNameQuery = (
    nextIncludes: string[],
    nextExcludes: string[],
  ) => {
    if (filterKey !== 'company_ids') return;
    const currentParams = getParamsToRead();
    const currentExactMatch =
      currentParams.get('company_name_exactMatch') === '1';
    const hasAnyValues = nextIncludes.length > 0 || nextExcludes.length > 0;

    handleQuery?.({
      filterKey: 'company_name',
      filterValue: {
        includedKeys: nextIncludes,
        excludedKeys: nextExcludes,
      },
      filterType: FilterComponentType.MULTILEVEL_CHECKBOX,
      filterExcludeKey: 'excludeCompany_name',
      // If there are no remaining values, ensure exactMatch flag is cleared.
      showExactMatch: hasAnyValues ? currentExactMatch : false,
    });
  };

  const showInputIncludeExclude = !hideIncludeExclude && inputValue;

  return (
    <>
      <div
        className={`input-container ${inputClassName} ${
          isDisabledByUnknown ? 'filter-disabled-by-unknown' : ''
        }`}
        id={suggestionsContainerID}
      >
        {inputLabel && <p className="blue-txt-15 label">{inputLabel}</p>}
        {!hideSearchBox && (
          <>
            {isDisabledByUnknown ? (
              <OverlayTooltip
                placement={Placement.Top}
                text={DISABLED_BY_UNKNOWN_TOOLTIP}
                className="bs-tooltip-inner disabled-by-unknown-tooltip"
              >
                <div className="position-relative disabled-filter-wrapper">
                  <input
                    id={searchInputContainerId}
                    ref={inputRef}
                    type="text"
                    className="auto-search-input position-relative"
                    autoComplete="off"
                    disabled
                    value={inputValue}
                    placeholder={inputPlaceHolder ?? 'Type and press Enter'}
                    maxLength={200}
                  />
                </div>
              </OverlayTooltip>
            ) : isIncludeDisabled &&
              (filterKey === 'competitors_company_name' ||
                filterKey === 'website_domain') ? (
              <OverlayTooltip
                placement={Placement.Top}
                text={includeTooltip}
                className="bs-tooltip-inner"
              >
                <div className="position-relative disabled-filter-wrapper">
                  <input
                    id={searchInputContainerId}
                    ref={inputRef}
                    type="text"
                    className="auto-search-input position-relative error-input-border"
                    autoComplete="off"
                    disabled
                    value={inputValue}
                    placeholder={inputPlaceHolder ?? 'Type and press Enter'}
                    maxLength={200}
                  />
                </div>
              </OverlayTooltip>
            ) : (
              <div className="position-relative">
                <input
                  id={searchInputContainerId}
                  ref={inputRef}
                  type="text"
                  className="auto-search-input position-relative"
                  autoComplete="off"
                  onChange={(e) => {
                    debouncedInputChangeRef.current.cancel();
                    const enteredValue = e?.target?.value;
                    debouncedInputChangeRef.current(enteredValue);
                    setInputValue(enteredValue);
                    // Clear any input error when user types
                    if (inputError) setInputError('');
                  }}
                  onKeyDown={onKeyDown}
                  value={inputValue}
                  placeholder={inputPlaceHolder ?? 'Type and press Enter'}
                  maxLength={200}
                />
                {/* Show validation error for overly long input */}
                {inputError && filterKey === 'company_ids' && (
                  <div
                    className="error-message mt-1"
                    style={{ fontSize: '12px', color: '#b91c1c' }}
                  >
                    {inputError}
                  </div>
                )}

                {showInputIncludeExclude && !disableCustomInput && (
                  <div className="include-exclude-icons position-absolute">
                    <OverlayTrigger
                      placement={Placement.Bottom}
                      overlay={
                        <Tooltip
                          id={`tooltip-${inputValue}-include-child-${inputValue}`}
                          className="bs-tooltip-inner"
                        >
                          {includeTooltip}
                        </Tooltip>
                      }
                    >
                      <CirclePlus
                        className={`circle-plus ${
                          isIncludeDisabled
                            ? 'include-disabled'
                            : 'include-enabled'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isIncludeDisabled) {
                            handleOnChange({
                              action: 'inputOption',
                              groupName: inputValue,
                            });
                            setShowAutoComplete(false);
                          }
                        }}
                      />
                    </OverlayTrigger>

                    <OverlayTrigger
                      placement={Placement.Bottom}
                      overlay={
                        <Tooltip
                          id={`tooltip-${inputValue}-exclude-child-${inputValue}`}
                          className="bs-tooltip-inner"
                        >
                          Exclude
                        </Tooltip>
                      }
                    >
                      <CircleMinus
                        className="circle-minus"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOnChange({
                            action: 'excludeOption',
                            groupName: inputValue,
                          });
                          setShowAutoComplete(false);
                        }}
                      />
                    </OverlayTrigger>
                  </div>
                )}
              </div>
            )}
            {advanceFilters?.showExactMatchCheckbox && (
              <div>
                <Checkbox
                  value={filterKey}
                  className="w-100 show-exact-match-check"
                  onChange={handleShowExactMatch}
                  checked={exactMatch}
                  disabled={
                    isDisabledByUnknown ||
                    (filterValues?.length === 0 && excludeValues?.length === 0)
                  }
                  label={
                    <div className="label-container d-inline-block mt-2">
                      <p className="checkbox-label m-0">Show Exact Match</p>
                    </div>
                  }
                />
              </div>
            )}
            {advanceFilters?.showIncludePastJobsCheckbox && (
              <div>
                <Checkbox
                  value={`${filterKey}_shouldSearchPast`}
                  className="w-100 should-search-past-filter"
                  onChange={handleShowIncludePastJobs}
                  checked={shouldSearchPastValues}
                  disabled={
                    isDisabledByUnknown ||
                    (filterValues?.length === 0 &&
                      excludeValues?.length === 0 &&
                      companyNameIncludeValues?.length === 0 &&
                      companyNameExcludeValues?.length === 0)
                  }
                  label={
                    <div className="label-container d-inline-block mt-2">
                      <p className="checkbox-label m-0">
                        Should Search Past Experiences
                      </p>
                    </div>
                  }
                />
              </div>
            )}

            {showAutoComplete && (suggestions?.length > 0 || isLoading) && (
              <RenderAutocomplete
                {...{
                  suggestions,
                  onSelectOption,
                  filterKey,
                  selectedSuggestionIndex,
                  handleOnChange,
                  setShowAutoComplete,
                  isIncludeDisabled,
                  includeTooltip,
                  isV2Response: isV2ResponseType,
                  isLoading,
                  hideIncludeExclude,
                  inputLabel,
                }}
              />
            )}
          </>
        )}
      </div>
      <div className="d-flex flex-column">
        {(filterValues?.length > 0 || companyNameIncludeValues.length > 0) && (
          <>
            {!hideIncludeExclude && (
              <p className="includes-excludes-text mt-2">Includes</p>
            )}
            <div className={`${hideIncludeExclude ? 'mb-2' : ''}`}>
              {filterValues
                ?.slice(0, expandedMaxFilterTags ?? filterValues.length)
                ?.map((selectedTag) => {
                  // For company_ids, resolve ID to name for display
                  const displayValue =
                    filterKey === 'company_ids'
                      ? effectiveCompanyIdNameMap[selectedTag] || selectedTag
                      : selectedTag;

                  return (
                    <span
                      key={`tag-${selectedTag}`}
                      className="d-inline-block"
                      onMouseEnter={() => setShowTagTooltip(selectedTag)}
                      onMouseLeave={() => setShowTagTooltip(null)}
                    >
                      <OverlayTooltip
                        placement={Placement.BottomStart}
                        text={displayValue}
                      >
                        <div>
                          <Tag
                            value={
                              displayValue?.length > 15
                                ? `${displayValue
                                    .substring(0, 15)
                                    .replace(/"/g, '')}..`
                                : displayValue.replace(/"/g, '')
                            }
                            variant={Tag.Variant.Remove}
                            className="filter-tag"
                            onClick={() =>
                              handleOnChange({
                                action: 'removeTag',
                                groupName: selectedTag, // Keep passing the ID for removal
                              })
                            }
                          />
                        </div>
                      </OverlayTooltip>
                    </span>
                  );
                })}
              {companyNameIncludeValues.map((value) => (
                <span
                  key={`company_name-tag-${value}`}
                  className="d-inline-block"
                  onMouseEnter={() =>
                    setShowTagTooltip(`company_name:${value}`)
                  }
                  onMouseLeave={() => setShowTagTooltip(null)}
                >
                  <OverlayTooltip
                    placement={Placement.BottomStart}
                    text={value}
                  >
                    <div>
                      <Tag
                        value={
                          value?.length > 15
                            ? `${value.substring(0, 15).replace(/"/g, '')}..`
                            : value.replace(/"/g, '')
                        }
                        variant={Tag.Variant.Remove}
                        className="filter-tag"
                        onClick={() => {
                          const nextIncludes = companyNameIncludeValues.filter(
                            (v) => v !== value,
                          );
                          updateCompanyNameQuery(
                            nextIncludes,
                            companyNameExcludeValues,
                          );
                        }}
                      />
                    </div>
                  </OverlayTooltip>
                </span>
              ))}
              {expandedMaxFilterTags &&
              filterValues?.length > expandedMaxFilterTags ? (
                <Tag
                  value={
                    <div className="d-flex align-items-center">
                      <Plus
                        className="gray-txt-15 mr-1"
                        width={13}
                        height={13}
                      />
                      <p className="mb-0">
                        {Array.from(filterValues)?.length -
                          expandedMaxFilterTags}
                      </p>
                    </div>
                  }
                  className="filter-tag more-tag"
                  onClick={() => handleExpandFilterTags?.()}
                />
              ) : null}
            </div>
          </>
        )}
        {!hideIncludeExclude &&
          (excludeValues?.length > 0 ||
            companyNameExcludeValues.length > 0) && (
            <>
              <p
                className={`includes-excludes-text ${
                  removeTopMargin && filterValues?.length === 0 ? 'mt-0' : ''
                }`}
              >
                Excludes
              </p>
              <div>
                {exactMatch && filterKey === 'location' ? (
                  <span
                    className="d-block w-100 tag-container"
                    onMouseEnter={() => setShowTagTooltip(excludeKey)}
                    onMouseLeave={() => setShowTagTooltip(null)}
                  >
                    <OverlayTooltip
                      placement={Placement.Bottom}
                      text={excludeValues?.join(',')}
                    >
                      <div>
                        <span className="filter-container-lead d-flex align-items-center justify-content-between">
                          <span className="filter-tag-exact-match">
                            {excludeValues?.join(',')}
                          </span>
                          <Icon
                            identifier="math-plus remove-icon"
                            onClick={() =>
                              handleOnChange({
                                action: 'removeTag',
                                groupName: excludeKey,
                                exclude: true,
                              })
                            }
                          />
                        </span>
                      </div>
                    </OverlayTooltip>
                  </span>
                ) : (
                  excludeValues?.map((selectedTag) => {
                    // For company_ids, resolve ID to name for display
                    const displayValue =
                      filterKey === 'company_ids'
                        ? effectiveCompanyIdNameMap[selectedTag] || selectedTag
                        : selectedTag;

                    return (
                      <span
                        key={`tag-${selectedTag}`}
                        className="d-inline-block"
                        onMouseEnter={() => setShowTagTooltip(selectedTag)}
                        onMouseLeave={() => setShowTagTooltip(null)}
                      >
                        <OverlayTrigger
                          placement={Placement.BottomStart}
                          overlay={getLabelTooltip(displayValue)}
                          show={
                            showTagTooltip === selectedTag &&
                            displayValue?.length > 15
                          }
                        >
                          <Tag
                            value={
                              displayValue?.length > 15
                                ? `${displayValue
                                    .substring(0, 15)
                                    .replace(/"/g, '')}..`
                                : displayValue.replace(/"/g, '')
                            }
                            variant={Tag.Variant.Remove}
                            className="filter-tag"
                            onClick={() =>
                              handleOnChange({
                                action: 'removeTag',
                                groupName: selectedTag, // Keep passing the ID for removal
                              })
                            }
                          />
                        </OverlayTrigger>
                      </span>
                    );
                  })
                )}
                {companyNameExcludeValues.map((value) => (
                  <span
                    key={`excludeCompany_name-tag-${value}`}
                    className="d-inline-block"
                    onMouseEnter={() =>
                      setShowTagTooltip(`excludeCompany_name:${value}`)
                    }
                    onMouseLeave={() => setShowTagTooltip(null)}
                  >
                    <OverlayTrigger
                      placement={Placement.BottomStart}
                      overlay={getLabelTooltip(value)}
                      show={
                        showTagTooltip === `excludeCompany_name:${value}` &&
                        value?.length > 15
                      }
                    >
                      <Tag
                        value={
                          value?.length > 15
                            ? `${value.substring(0, 15).replace(/"/g, '')}..`
                            : value.replace(/"/g, '')
                        }
                        variant={Tag.Variant.Remove}
                        className="filter-tag"
                        onClick={() => {
                          const nextExcludes = companyNameExcludeValues.filter(
                            (v) => v !== value,
                          );
                          updateCompanyNameQuery(
                            companyNameIncludeValues,
                            nextExcludes,
                          );
                        }}
                      />
                    </OverlayTrigger>
                  </span>
                ))}
              </div>
            </>
          )}
        {checkboxes?.filter((item) =>
          initialCheckboxes.some((initial) => initial?.key === item?.key),
        )?.length > 0 && (
          <div
            className={classNames('v2-filter-checkbox-list', {
              'mt-0':
                removeTopMargin &&
                filterValues?.length === 0 &&
                excludeValues?.length === 0,
            })}
          >
            {checkboxes
              ?.filter((item) =>
                initialCheckboxes.some((initial) => initial?.key === item?.key),
              )
              ?.map((item) => (
                <RenderCheckboxGroup
                  key={`item-${item.key}`}
                  name={item.key}
                  label={item?.value}
                  checked={item.checked as boolean | 'indeterminate'}
                  childItems={item.children}
                  expanded={expanded}
                  handleOnChange={handleOnChange}
                  filterKey={filterKey}
                  infoCircleToolTip={infoCircleToolTipMapping?.[item?.key]}
                  hideIncludeExclude={hideIncludeExclude}
                  isIncludeDisabled={isIncludeDisabled}
                  includeTooltip={includeTooltip}
                  isPhoneNumberNudgeOpen={isPhoneNumberNudgeOpen}
                  onToggleExpand={handleToggleExpand}
                  tooltipText={(item as CheckboxItem)?.tooltipText}
                  parentLocked={(item as CheckboxItem)?.isLocked}
                  onLockClick={onLockedClick}
                />
              ))}
          </div>
        )}
      </div>
    </>
  );
};

export default V2FilterCheckbox;
